import * as THREE from 'three';
import { Sky } from 'three/addons/objects/Sky.js';

const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _center = new THREE.Vector3();
const _camFwd = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);
const UP_ALT = new THREE.Vector3(0, 0, 1);

/**
 * Physical sky + key/fill/bounce rig + IBL.
 *
 * Exposure discipline: the composer renders to a HalfFloat target, so material
 * shaders skip tone mapping entirely and OutputPass does it at the end. That
 * means everything in the scene is scene-referred *linear* radiance and the
 * Preetham sky returns values around 1.0-1.4 at the horizon. At exposure 1.0
 * ACES maps that straight to white, which is exactly why the old sky was a flat
 * grey card. Exposure sits at 0.55; the sky is trimmed separately (uSkyTrim)
 * so its brightness is not hostage to the key.
 *
 * The key is calibrated against Materials.js's measured reflectances (concrete
 * 0.31, asphalt 0.045, sand 0.33, plaster 0.58 linear). At intensity 9.0 and
 * exposure 0.55 that lands sunlit asphalt near sRGB 0.30 and sunlit paving near
 * sRGB 0.55 - which is where a photograph of a sunlit street actually sits.
 * Asphalt is *supposed* to be the darkest thing in frame.
 *
 * Lighting is key-dominant but not key-only: the hemisphere fill is deliberately
 * blue and strong enough to keep detail in the shade side, because the lit/shade
 * separation here is carried by hue (warm key vs cool sky fill) as much as by
 * value. Fill that is too weak reads as dead mud, fill that is too flat kills
 * the form; the ratio, not either number alone, is the thing being tuned.
 */
export class Atmosphere {
  /**
   * Every light value is an option defaulting to the daylight rig, so a level
   * can declare its own mood without this class knowing about specific maps.
   * `skyGradient: [zenithHex, horizonHex]` swaps the Preetham sky for a flat
   * gradient dome — needed for night, where a physical daytime sky model at low
   * sun elevation gives you a sunset, not darkness.
   */
  constructor(engine, {
    elevation = 40, azimuth = 122, turbidity = 1.9,
    exposure = 0.55,
    rayleigh = 3.6, mie = 0.003, mieG = 0.972, skyTrim = 0.38,
    sunColor = 0xffcf96, sunIntensity = 15.0,
    hemiSky = 0x8fb8ff, hemiGround = 0x2e2418, hemiIntensity = 0.50,
    bounceColor = 0xffbc7d, bounceIntensity = 0.20,
    envIntensity = 0.26, vmEnvIntensity = 0.45,
    skyGradient = null,
    shadowStrength = 1,
    clouds = { coverage: 0.42 },
  } = {}) {
    this.engine = engine;
    engine.atmosphere = this; // PostFX reads sun direction/colour from here.
    const { scene, renderer } = engine;

    this.exposure = exposure;
    this.envIntensity = envIntensity;
    this.vmEnvIntensity = vmEnvIntensity;
    this.skyGradient = skyGradient;
    renderer.toneMappingExposure = this.exposure;

    if (skyGradient) {
      this.sky = this._gradientSky(skyGradient);
      scene.add(this.sky);
    } else {
    this.sky = new Sky();
    this.sky.scale.setScalar(20000);
    const u = this.sky.material.uniforms;
    u.turbidity.value = turbidity;
    u.rayleigh.value = rayleigh; // deep blue zenith instead of milky white
    u.mieCoefficient.value = mie;
    u.mieDirectionalG.value = mieG; // tight forward lobe => readable sun disc

    // Sky luminance trim. Preetham returns ~1.0-1.4 at the horizon, which ACES
    // maps to near-white at any sane exposure, so the sky always clipped and
    // dragged the whole grade with it. Trimming the sky inside its own shader
    // lets the scene keep a comfortable key exposure while the sky sits where
    // it still shows a gradient. This is also a *saturation* control: ACES
    // desaturates hard in the shoulder, so a sky that is too bright goes pale
    // cyan-white no matter how much rayleigh you dial in. The IBL bakes from
    // this same material, so environmentIntensity compensates for the trim.
    u.uSkyTrim = { value: skyTrim };
    this.sky.material.fragmentShader =
      'uniform float uSkyTrim;\n' + this.sky.material.fragmentShader.replace(
        'gl_FragColor = vec4( retColor, 1.0 );',
        'gl_FragColor = vec4( retColor * uSkyTrim, 1.0 );'
      );
    this.sky.material.needsUpdate = true;
    scene.add(this.sky);
    }

    // Cloud deck. `clouds: null` opts out entirely.
    this.clouds = clouds ? this._cloudLayer(clouds) : null;
    if (this.clouds) scene.add(this.clouds);
    this.cloudTime = 0;

    this.sunDir = new THREE.Vector3();
    this.setSun(elevation, azimuth);

    // Key light. Mid-afternoon sun raking across the street from the east so
    // one facade line is hot and the other is in shade, with the shadow edge
    // cutting a diagonal down the roadway. Any lower and the buildings shadow
    // the whole street floor; any higher and the shadows stop reading.
    this.sunColor = new THREE.Color(sunColor);
    this.sun = new THREE.DirectionalLight(this.sunColor.getHex(), sunIntensity);
    this.sun.castShadow = true;
    this.shadowExtent = 30;
    this.shadowDistance = 100;
    this.sun.shadow.mapSize.set(3072, 3072);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 200;
    const s = this.shadowExtent;
    Object.assign(this.sun.shadow.camera, { left: -s, right: s, top: s, bottom: -s });
    this.sun.shadow.camera.updateProjectionMatrix();
    // Tight bias: 3072 over a 60m frustum is ~2cm/texel, so we can afford a
    // small constant bias and lean on normalBias for grazing angles.
    this.sun.shadow.bias = -0.00016;
    this.sun.shadow.normalBias = 0.018;
    this.sun.shadow.blurSamples = 8;
    this.sun.target.position.set(0, 0, 0);
    // setSun() ran before `this.sun` existed, so place the light explicitly.
    // Without this the sun sits at its default (0,1,0) - straight overhead,
    // 1m above the origin - until the first update(), which never happens while
    // the game is paused at the deploy prompt. That is what a "no sun direction,
    // everything lit from above" frame looks like.
    this.sun.position.copy(this.sunDir).multiplyScalar(this.shadowDistance);
    scene.add(this.sun, this.sun.target);

    // Sky fill. Deliberately blue: the lit/shade separation in this frame is
    // carried as much by hue (warm key vs cool shade) as by value, which is
    // what stops the shadow side from turning into dead brown mud.
    this.fill = new THREE.HemisphereLight(hemiSky, hemiGround, hemiIntensity);
    scene.add(this.fill);

    // Bounce off the sunlit facade back across the street. Warm, no shadow.
    this.bounce = new THREE.DirectionalLight(bounceColor, bounceIntensity);
    this.bounce.position.copy(this.sunDir).multiplyScalar(-60);
    this.bounce.position.y = 14;
    scene.add(this.bounce, this.bounce.target);

    // Aerial perspective is done in PostFX (depth-based, height-falloff,
    // sun-tinted). Scene fog stays off so it can't double up.
    scene.fog = null;

    this.refreshEnvironment();
  }

  /**
   * Flat gradient dome, for skies the Preetham model cannot express. A physical
   * daytime sky at low sun elevation gives you a sunset; a night map needs an
   * unlit gradient whose colours come from the map's own skybox.
   */
  _gradientSky([zenith, horizon]) {
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        uZenith: { value: new THREE.Color(zenith) },
        uHorizon: { value: new THREE.Color(horizon) },
      },
      vertexShader: /* glsl */`
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */`
        uniform vec3 uZenith, uHorizon;
        varying vec3 vDir;
        void main() {
          // Bias the blend toward the horizon so the band reads over the
          // rooftops rather than only straight overhead.
          float t = pow(clamp(normalize(vDir).y * 0.5 + 0.5, 0.0, 1.0), 0.65);
          gl_FragColor = vec4(mix(uHorizon, uZenith, t), 1.0);
        }
      `,
    });
    // Radius must sit INSIDE the camera's far plane (600) or the dome is
    // clipped away entirely and the sky renders black. Depth testing is off and
    // it draws first, so world geometry always covers it regardless of radius.
    mat.depthTest = false;
    const sky = new THREE.Mesh(new THREE.SphereGeometry(480, 32, 20), mat);
    sky.frustumCulled = false;
    sky.renderOrder = -1000;
    return sky;
  }

  /**
   * Procedural cloud layer.
   *
   * Drawn on its own dome between the sky and the world. The important trick is
   * that clouds are NOT painted on the dome surface — the fragment shader
   * intersects each view ray with a horizontal plane at `altitude` and samples
   * noise at that world position. That gives real perspective: cloud cells are
   * large overhead and compress toward the horizon, which is what makes a sky
   * read as a ceiling at a finite height rather than a painted backdrop.
   *
   * Lighting is a cheap two-term model that nonetheless sells it: density is
   * sampled again a short distance toward the sun to get self-shadowing, and a
   * forward-scattering term puts a bright rim on the sun side of each cell.
   */
  _cloudLayer({
    coverage = 0.45, density = 1.0, altitude = 900, scale = 0.0016,
    wind = [1, 0.35], speed = 0.004, tint = 0xffffff, shade = 0x6b7183,
    detail = 1.0, opacity = 1.0,
  } = {}) {
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      // depthTest MUST stay on. A transparent mesh is drawn after all opaque
      // geometry regardless of renderOrder, so with depth testing off the cloud
      // deck painted straight over the buildings. The dome sits at radius 470,
      // well inside the far plane, so the depth test correctly hides it behind
      // anything nearer.
      depthTest: true,
      uniforms: {
        uTime: { value: 0 },
        uSunDir: { value: new THREE.Vector3(0, 1, 0) },
        uSunColor: { value: new THREE.Color(0xffe6c0) },
        uTint: { value: new THREE.Color(tint) },
        uShade: { value: new THREE.Color(shade) },
        uCoverage: { value: coverage },
        uDensity: { value: density },
        uAltitude: { value: altitude },
        uScale: { value: scale },
        uWind: { value: new THREE.Vector2(wind[0], wind[1]) },
        uSpeed: { value: speed },
        uDetail: { value: detail },
        uOpacity: { value: opacity },
      },
      vertexShader: /* glsl */`
        varying vec3 vDir;
        void main() {
          vDir = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */`
        precision highp float;
        uniform float uTime, uCoverage, uDensity, uAltitude, uScale, uSpeed, uDetail, uOpacity;
        uniform vec2 uWind;
        uniform vec3 uSunDir, uSunColor, uTint, uShade;
        varying vec3 vDir;

        vec2 hash2(vec2 p) {
          p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
          return fract(sin(p) * 43758.5453) * 2.0 - 1.0;
        }
        float gnoise(vec2 p) {
          vec2 i = floor(p), f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(dot(hash2(i),                f),
                         dot(hash2(i + vec2(1, 0)), f - vec2(1, 0)), u.x),
                     mix(dot(hash2(i + vec2(0, 1)), f - vec2(0, 1)),
                         dot(hash2(i + vec2(1, 1)), f - vec2(1, 1)), u.x), u.y);
        }
        float fbm(vec2 p) {
          float a = 0.5, s = 0.0, n = 0.0;
          for (int i = 0; i < 5; i++) {
            s += a * gnoise(p);
            n += a;
            // Rotate each octave so the cells don't line up into a grid.
            p = mat2(1.6, 1.2, -1.2, 1.6) * p;
            a *= 0.5 * uDetail;
          }
          return s / max(n, 1e-4);
        }

        /** Cloud density at a world XZ position, 0..1. */
        float clouds(vec2 q) {
          q += uWind * uTime * uSpeed * 100.0;
          // Normalise into 0..1. Gradient noise summed over octaves lands
          // roughly in -0.7..0.7, so scale by 0.85 rather than 0.5 — mapping it
          // as if it reached +-1 meant the threshold below could never be
          // crossed and the sky stayed permanently clear.
          float f = clamp(fbm(q) * 0.85 + 0.5, 0.0, 1.0);
          // Coverage picks the threshold: 0 -> only the densest cells survive,
          // 1 -> almost everything is cloud. The band above it is the soft edge.
          float thr = mix(0.72, 0.12, clamp(uCoverage, 0.0, 1.0));
          return smoothstep(thr, thr + 0.24, f);
        }

        void main() {
          vec3 d = normalize(vDir);
          // Below the horizon there is no cloud deck to see.
          if (d.y < 0.02) discard;

          // Intersect the view ray with the cloud plane. Cells compress toward
          // the horizon for free, because t grows as d.y shrinks.
          float t = uAltitude / d.y;
          vec2 q = d.xz * t * uScale;

          float c = clouds(q) * uDensity;
          if (c < 0.004) discard;

          // Self-shadowing: sample again a step toward the sun. If there is
          // cloud between this point and the light, this cell is in shadow.
          vec2 toSun = normalize(uSunDir.xz + vec2(1e-4)) * 0.30;
          float lit = 1.0 - clamp(clouds(q + toSun) * 0.85, 0.0, 1.0);

          // Forward scattering: bright silver rim where we look toward the sun.
          float fwd = pow(max(dot(d, normalize(uSunDir)), 0.0), 6.0);

          vec3 col = mix(uShade, uTint, lit * 0.75 + 0.25);
          col += uSunColor * fwd * 0.55 * lit;
          // Thin edges scatter more light than thick cores.
          col *= mix(1.18, 0.92, smoothstep(0.2, 0.9, c));

          // Fade the deck out at the horizon so it never ends in a hard line.
          float horizon = smoothstep(0.02, 0.20, d.y);
          gl_FragColor = vec4(col, clamp(c, 0.0, 1.0) * horizon * uOpacity);
        }
      `,
    });

    const dome = new THREE.Mesh(new THREE.SphereGeometry(470, 40, 24), mat);
    dome.frustumCulled = false;
    // After the sky (-1000), before everything else.
    dome.renderOrder = -999;
    return dome;
  }

  setSun(elevationDeg, azimuthDeg) {
    this.elevation = elevationDeg;
    this.azimuth = azimuthDeg;
    const phi = THREE.MathUtils.degToRad(90 - elevationDeg);
    const theta = THREE.MathUtils.degToRad(azimuthDeg);
    this.sunDir.setFromSphericalCoords(1, phi, theta);
    // The gradient dome has no sun disc to move.
    this.sky.material.uniforms?.sunPosition?.value.copy(this.sunDir);
    if (this.sun) {
      this.sun.position.copy(this.sunDir).multiplyScalar(this.shadowDistance);
      this.sun.target.updateMatrixWorld();
    }
  }

  /** Re-bake the IBL from the current sky. Cheap enough to call on sun changes. */
  refreshEnvironment() {
    const { scene, pmrem } = this.engine;
    this.envRT?.dispose();
    const skyScene = new THREE.Scene();
    skyScene.add(this.sky);
    // Bake the cloud deck into the IBL too, so an overcast sky actually dims and
    // neutralises the ambient instead of the clouds being a decal you can see
    // through in the lighting.
    if (this.clouds) skyScene.add(this.clouds);
    this.envRT = pmrem.fromScene(skyScene, 0.04);
    scene.add(this.sky);
    if (this.clouds) scene.add(this.clouds);
    scene.environment = this.envRT.texture;
    // Dimmed hard: the sky IBL is bright and omnidirectional, and screen-space
    // AO only claws back part of it. At 1.0 the ambient was ~3x the key on
    // shaded surfaces and no cast shadow was legible anywhere in the frame.
    scene.environmentIntensity = this.envIntensity;
    this.engine.viewmodelScene.environment = this.envRT.texture;
    this.engine.viewmodelScene.environmentIntensity = this.vmEnvIntensity;
  }

  /**
   * Keep the shadow frustum centred slightly ahead of the player so the 4096
   * map is spent on what is actually on screen, and snap the centre to whole
   * shadow texels so the shadow edges don't crawl when the player walks.
   */
  update(playerPosition, dt = 0.016) {
    const cam = this.engine.camera;

    if (this.clouds) {
      const u = this.clouds.material.uniforms;
      this.cloudTime += dt;
      u.uTime.value = this.cloudTime;
      u.uSunDir.value.copy(this.sunDir);
      u.uSunColor.value.copy(this.sunColor);
      // The dome rides with the camera so the deck never runs out; the noise is
      // sampled in world space, so the clouds themselves stay put.
      this.clouds.position.set(cam.position.x, 0, cam.position.z);
    }

    _camFwd.set(0, 0, -1).applyQuaternion(cam.quaternion);
    _camFwd.y = 0;
    if (_camFwd.lengthSq() < 1e-6) _camFwd.set(0, 0, -1);
    _camFwd.normalize();

    _center.copy(playerPosition).addScaledVector(_camFwd, this.shadowExtent * 0.45);
    _center.y = Math.max(_center.y, 1.0);

    // Snap in light space.
    _fwd.copy(this.sunDir).negate().normalize();
    _up.copy(Math.abs(_fwd.y) > 0.99 ? UP_ALT : UP);
    _right.crossVectors(_up, _fwd).normalize();
    _up.crossVectors(_fwd, _right).normalize();

    const texel = (2 * this.shadowExtent) / this.sun.shadow.mapSize.x;
    const cx = _center.dot(_right);
    const cy = _center.dot(_up);
    _center.addScaledVector(_right, Math.round(cx / texel) * texel - cx);
    _center.addScaledVector(_up, Math.round(cy / texel) * texel - cy);

    this.sun.position.copy(_center).addScaledVector(this.sunDir, this.shadowDistance);
    this.sun.target.position.copy(_center);
    this.sun.target.updateMatrixWorld();

    this.bounce.position.copy(playerPosition).addScaledVector(this.sunDir, -50);
    this.bounce.position.y = playerPosition.y + 12;
    this.bounce.target.position.copy(playerPosition);
    this.bounce.target.updateMatrixWorld();
  }
}
